import Phaser from 'phaser';
import { GAME_CONFIG } from '../config';
import { STAGES, getAccessoryForStage, StageData } from '../data/stages';
import { stageProgress, formatCooldown } from '../state/StageProgress';
import { COLORS, CSS, FONT, OUTLINE } from '../theme';
import { drawOutlinedRect, drawOutlinedCircle } from '../ui/OutlinedShape';
import { makePill } from '../ui/Pill';
import { setLetterboxColor } from '../utils/letterbox';
import { getLayoutFrame } from '../utils/layout';

/** Pointer travel (px) beyond which a press is treated as a scroll gesture, not a tap. */
const SCROLL_DRAG_THRESHOLD = 8;

/** Per-card refs for live cooldown updates. The text label is mutated in
 * `update()` once per second; when a cooldown expires the scene restarts so
 * the card transforms back into a normal PLAY tile. */
interface CooldownBinding {
  stageId: string;
  label: Phaser.GameObjects.Text;
}

export class StageSelectScene extends Phaser.Scene {
  private cardsContainer!: Phaser.GameObjects.Container;
  private scrollMinY = 0;
  private scrollMaxY = 0;
  private cooldownBindings: CooldownBinding[] = [];
  private lastCooldownTick = 0;

  constructor() {
    super('StageSelectScene');
  }

  create() {
    const { STAGE_W, STAGE_H } = GAME_CONFIG;
    const frame = getLayoutFrame(this);
    this.drawBackdrop();

    // Reset per-create state so a scene restart starts with clean tracking.
    this.cooldownBindings = [];
    this.lastCooldownTick = 0;

    // Scrollable container for stage cards. Header UI is drawn at a higher depth
    // so cards visually slide underneath it as the user scrolls.
    this.cardsContainer = this.add.container(0, 0);

    // Stage cards grid (2 columns). Tile dimensions sized so icon + name + stars
    // + meta line + PLAY button have visible breathing room above the bottom CTA.
    const colCount = 2;
    const tileW = 322;
    const tileH = 440;
    const gapX = 32;
    const gapY = 24;
    const totalW = colCount * tileW + (colCount - 1) * gapX;
    const startX = (STAGE_W - totalW) / 2 + tileW / 2;
    // Top of the first card (startY - tileH/2) sits just below the fixed
    // header divider at y=258 with a small gap.
    const startY = frame.fromTop(500);

    STAGES.forEach((stage, i) => {
      const col = i % colCount;
      const row = Math.floor(i / colCount);
      const x = startX + col * (tileW + gapX);
      const y = startY + row * (tileH + gapY);
      this.drawTile(x, y, tileW, tileH, stage);
    });

    // Compute scroll bounds based on actual content extents so adding more
    // stages automatically extends the scrollable area. The bottom edge is
    // unmarked — cards scroll all the way to the canvas edge and the user
    // can scroll until the last row sits fully visible above it.
    const rows = Math.ceil(STAGES.length / colCount);
    const contentBottomY = startY + (rows - 1) * (tileH + gapY) + tileH / 2 + 24;
    const viewBottomY = frame.fromBottom(20);
    this.scrollMaxY = 0;
    this.scrollMinY = Math.min(0, viewBottomY - contentBottomY);

    // ───── Header UI (stays fixed; cards scroll beneath) ─────
    const headerDepth = 100;

    const back = makePill(this, 90, frame.fromTop(72), {
      text: '← HOME',
      textSize: 22,
      textColor: CSS.ink,
      fontWeight: '800',
      padX: 20,
      padY: 12,
      fill: COLORS.white,
      fillAlpha: 1,
      border: COLORS.ink,
      borderAlpha: 1,
      borderThickness: 4,
    });
    back.setDepth(headerDepth);
    // Separate Zone for hit detection — Container-with-rect hit areas were
    // proving flaky in Phaser, and the rest of the codebase uses Zones for
    // every other tap target. Hit area is generous so finger taps
    // around the pill still register.
    const backHit = this.add
      .zone(90, frame.fromTop(72), 200, 76)
      .setInteractive({ useHandCursor: true });
    backHit.setDepth(headerDepth + 1);
    backHit.on('pointerup', (p: Phaser.Input.Pointer) => {
      if (p.getDistance() > SCROLL_DRAG_THRESHOLD) return;
      this.scene.start('HomeScene');
    });

    makePill(this, STAGE_W - 120, frame.fromTop(72), {
      text: `✦ ${stageProgress.getPoints()}p`,
      textSize: 26,
      textColor: CSS.ink,
      fontWeight: '800',
      padX: 24,
      padY: 14,
      fill: COLORS.yolk,
      fillAlpha: 1,
      border: COLORS.ink,
      borderAlpha: 1,
      borderThickness: 4,
    }).setDepth(headerDepth);

    this.add
      .text(STAGE_W / 2, frame.fromTop(138), 'STAGE SELECT', {
        fontFamily: FONT.display,
        fontSize: '24px',
        color: CSS.inkSoft,
        fontStyle: '800',
      })
      .setOrigin(0.5)
      .setLetterSpacing(3)
      .setDepth(headerDepth);

    this.add
      .text(STAGE_W / 2, frame.fromTop(188), 'Pick a Stage', {
        fontFamily: FONT.display,
        fontSize: '48px',
        color: CSS.ink,
        fontStyle: '800',
      })
      .setOrigin(0.5)
      .setDepth(headerDepth);

    this.add
      .text(
        STAGE_W / 2,
        frame.fromTop(232),
        `Toss the perfect hat  ·  TOTAL ${stageProgress.totalCumulativeScore()} pts`,
        {
          fontFamily: FONT.display,
          fontSize: '20px',
          color: CSS.inkSoft,
          fontStyle: '700',
        },
      )
      .setOrigin(0.5)
      .setDepth(headerDepth);

    // Cream strip below the header to hide cards that scroll up past it,
    // plus a thin ink divider on its bottom edge so the boundary between
    // fixed header and scrollable grid is unambiguous. The divider sits at
    // depth headerDepth so it paints on top of the strip but under the pills.
    // The strip covers from y=0 (canvas top, which may include OS notch area)
    // down to the safe-zone-anchored divider so cards never leak past the
    // header on notched devices.
    const headerBottomY = frame.fromTop(260);
    const headerMask = this.add.graphics();
    headerMask.fillStyle(COLORS.cream, 1);
    headerMask.fillRect(0, 0, STAGE_W, headerBottomY);
    headerMask.setDepth(headerDepth - 1);

    const divider = this.add.graphics();
    divider.fillStyle(COLORS.ink, 0.12);
    divider.fillRect(36, headerBottomY - 2, STAGE_W - 72, 2);
    divider.setDepth(headerDepth);

    this.setupScroll();

    this.game.events.on('safeAreaChanged', this.handleSafeAreaChanged, this);
    this.events.once('shutdown', () => {
      this.game.events.off('safeAreaChanged', this.handleSafeAreaChanged, this);
    });
  }

  private handleSafeAreaChanged = () => {
    if (!this.scene.isActive()) return;
    this.scene.restart();
  };

  /** Tick cooldown labels at ~1Hz. When any tracked cooldown expires we
   * restart the scene so the affected card transforms back into a PLAY tile.
   * Restart is cheap here (5 cards, no async assets to reload). */
  update(time: number) {
    if (this.cooldownBindings.length === 0) return;
    if (time - this.lastCooldownTick < 1000) return;
    this.lastCooldownTick = time;

    let anyExpired = false;
    for (const binding of this.cooldownBindings) {
      const remain = stageProgress.cooldownRemainingMs(binding.stageId);
      if (remain <= 0) {
        anyExpired = true;
      } else {
        binding.label.setText(`REFILL · ${formatCooldown(remain)}`);
      }
    }
    if (anyExpired) this.scene.restart();
  }

  /** Vertical scroll via drag + mouse wheel, clamped to content bounds. */
  private setupScroll() {
    let dragging = false;
    let dragStartPointerY = 0;
    let scrollStart = 0;

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      dragging = true;
      dragStartPointerY = p.y;
      scrollStart = this.cardsContainer.y;
    });

    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!dragging || !p.isDown) return;
      const dy = p.y - dragStartPointerY;
      this.cardsContainer.y = Phaser.Math.Clamp(
        scrollStart + dy,
        this.scrollMinY,
        this.scrollMaxY,
      );
    });

    this.input.on('pointerup', () => {
      dragging = false;
    });
    this.input.on('pointerupoutside', () => {
      dragging = false;
    });

    this.input.on(
      'wheel',
      (_p: Phaser.Input.Pointer, _objs: unknown, _dx: number, dy: number) => {
        this.cardsContainer.y = Phaser.Math.Clamp(
          this.cardsContainer.y - dy * 0.5,
          this.scrollMinY,
          this.scrollMaxY,
        );
      },
    );
  }

  private drawBackdrop() {
    const { STAGE_W, STAGE_H } = GAME_CONFIG;
    setLetterboxColor(COLORS.cream);
    const g = this.add.graphics();
    g.fillStyle(COLORS.cream, 1);
    g.fillRect(0, 0, STAGE_W, STAGE_H);
    // Subtle dotted texture
    g.fillStyle(COLORS.ink, 0.05);
    for (let yy = 0; yy < STAGE_H; yy += 28) {
      for (let xx = 14; xx < STAGE_W; xx += 28) {
        g.fillCircle(xx + (yy % 56 === 0 ? 14 : 0), yy, 1.5);
      }
    }
  }

  private drawTile(x: number, y: number, w: number, h: number, stage: StageData) {
    const unlocked = stageProgress.isUnlocked(stage);
    const record = stageProgress.getRecord(stage.id);
    const acc = getAccessoryForStage(stage);

    // Card body
    const card = drawOutlinedRect(this, x, y, w, h, {
      fill: unlocked ? COLORS.white : COLORS.paper,
      radius: 36,
      shadow: 8,
      outline: OUTLINE,
    });
    card.setAlpha(unlocked ? 1 : 0.9);
    this.cardsContainer.add(card);

    // Stage number pill at top-left of card
    const pill = makePill(this, x - w / 2 + 64, y - h / 2 + 36, {
      text: `STAGE ${String(stage.index).padStart(2, '0')}`,
      textSize: 18,
      textColor: CSS.white,
      fontWeight: '800',
      padX: 16,
      padY: 8,
      fill: COLORS.ink,
      fillAlpha: 1,
    });
    this.cardsContainer.add(pill);

    // Big circular badge with accessory icon
    const diskR = 88;
    const palette = [COLORS.yolk, COLORS.coral, COLORS.rose, COLORS.emerald, COLORS.plum];
    const idx = stage.index - 1;
    const diskFill = unlocked ? palette[idx % palette.length] : COLORS.paper;
    const diskY = y - 72;
    const disk = drawOutlinedCircle(this, x, diskY, diskR, diskFill, COLORS.ink, OUTLINE);
    this.cardsContainer.add(disk);

    // Accessory icon inside disk
    if (this.textures.exists(acc.accIconKey)) {
      const icon = this.add.image(x, diskY, acc.accIconKey);
      const tex = icon.texture.getSourceImage() as HTMLImageElement;
      const maxSize = 128;
      const scale = Math.min(maxSize / tex.width, maxSize / tex.height);
      icon.setScale(scale);
      icon.setAlpha(unlocked ? 1 : 0.35);
      this.cardsContainer.add(icon);
    }

    // Stage name
    const nameText = this.add
      .text(x, y + 64, stage.name, {
        fontFamily: FONT.display,
        fontSize: '28px',
        color: unlocked ? CSS.ink : CSS.inkFaint,
        fontStyle: '800',
      })
      .setOrigin(0.5);
    this.cardsContainer.add(nameText);

    // Stars row
    const starGap = 32;
    const starsBaseY = y + 100;
    for (let s = 0; s < 3; s++) {
      const filled = s < record.stars;
      const sx = x - starGap + s * starGap;
      const star = this.add.graphics({ x: sx, y: starsBaseY });
      this.drawStarShape(star, 14, filled ? COLORS.yolk : COLORS.white, unlocked);
      this.cardsContainer.add(star);
    }

    if (unlocked) {
      // PLAY button anchored to the bottom of the card. When the stage is in
      // cooldown after a fully-failed run, the button switches to a yolk-tinted
      // "REFILL · Nh Nm" badge and tapping still enters the scene — GameScene
      // detects the cooldown and shows the refill modal directly.
      const cooldownMs = stageProgress.cooldownRemainingMs(stage.id);
      const inCooldown = cooldownMs > 0;
      const btnY = y + h / 2 - 54;
      const btnH = 60;
      const btn = drawOutlinedRect(this, x, btnY, w - 64, btnH, {
        fill: inCooldown ? COLORS.yolk : COLORS.coral,
        radius: 32,
        shadow: 6,
        outline: OUTLINE,
      });
      this.cardsContainer.add(btn);

      const btnText = this.add
        .text(
          x,
          btnY,
          inCooldown ? `REFILL · ${formatCooldown(cooldownMs)}` : 'PLAY  ▶',
          {
            fontFamily: FONT.display,
            fontSize: inCooldown ? '22px' : '28px',
            color: inCooldown ? CSS.ink : CSS.white,
            fontStyle: '800',
          },
        )
        .setOrigin(0.5)
        .setLetterSpacing(2);
      this.cardsContainer.add(btnText);

      if (inCooldown) {
        this.cooldownBindings.push({ stageId: stage.id, label: btnText });
      }

      const hit = this.add
        .zone(x, btnY, w - 64, btnH)
        .setInteractive({ useHandCursor: true });
      hit.on('pointerup', (p: Phaser.Input.Pointer) => {
        if (p.getDistance() > SCROLL_DRAG_THRESHOLD) return;
        this.scene.start('GameScene', { stageId: stage.id });
      });
      this.cardsContainer.add(hit);
    } else {
      // Locked: centered LOCKED label + hint, no BPM line
      const locked = this.add
        .text(x, y + 152, 'LOCKED', {
          fontFamily: FONT.display,
          fontSize: '24px',
          color: CSS.inkFaint,
          fontStyle: '900',
        })
        .setOrigin(0.5)
        .setLetterSpacing(3);
      this.cardsContainer.add(locked);
      const hint = this.add
        .text(x, y + 186, 'Clear the previous stage', {
          fontFamily: FONT.display,
          fontSize: '18px',
          color: CSS.inkFaint,
          fontStyle: '700',
        })
        .setOrigin(0.5);
      this.cardsContainer.add(hint);
    }
  }

  private drawStarShape(g: Phaser.GameObjects.Graphics, r: number, fill: number, unlocked: boolean) {
    const points: { x: number; y: number }[] = [];
    for (let i = 0; i < 10; i++) {
      const angle = (Math.PI * 2 * i) / 10 - Math.PI / 2;
      const radius = i % 2 === 0 ? r : r * 0.42;
      points.push({ x: Math.cos(angle) * radius, y: Math.sin(angle) * radius });
    }
    g.fillStyle(fill, unlocked ? 1 : 0.4);
    g.beginPath();
    g.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) g.lineTo(points[i].x, points[i].y);
    g.closePath();
    g.fillPath();
    g.lineStyle(2, COLORS.ink, unlocked ? 1 : 0.4);
    g.strokePath();
  }
}
